<template>
  <BaseModalEditCreate
    :mode="mode"
    :is-modified="isModified"
    @save="onSave"
    @dismiss-changes="onDismissChanges">
    <template #form>
      <div>
        <label for="description" class="text-sm font-medium">{{
          $t('qst-actions.description')
        }}</label>
        <BaseInput
          v-model="modifiedData.description"
          color="base"
          show-error-as-tooltip
          :error="$v.description.required.$invalid ? $v.description.required.$message : ''" />
      </div>
    </template>
  </BaseModalEditCreate>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';

import BaseInput from '@/components/common/BaseInput.vue';
import BaseModalEditCreate from '@/components/common/BaseModalEditCreate.vue';

import { type QSTAction } from '@/types/QSTActions';
import { deepCopy } from '@/utils/data';

const { t: $t } = useI18n({ useScope: 'global' });

const emit = defineEmits(['closeModal', 'save']);

const props = withDefaults(
  defineProps<{
    mode: 'add' | 'edit';
    qstAction: QSTAction;
  }>(),
  {}
);

const modifiedData = ref<QSTAction>(deepCopy<QSTAction>(props.qstAction));
const isModified = ref<boolean>(false);

watch(
  () => modifiedData.value,
  (newData) => {
    validationModel.value.description = newData.description || '';

    const valueChanged = JSON.stringify(newData) !== JSON.stringify(props.qstAction);
    isModified.value = valueChanged;
  },
  { deep: true }
);

function onDismissChanges() {
  modifiedData.value = deepCopy<QSTAction>(props.qstAction);
  emit('closeModal');
}

async function onSave() {
  $v.value.$touch();
  if ($v.value.$invalid) return;
  emit('save', modifiedData.value);
}

const validationModel = ref({
  description: modifiedData.value.description || '',
});

const rules = computed(() => ({
  description: { required: helpers.withMessage('Description is required', required) },
}));

const $v = useVuelidate(rules, validationModel, { $lazy: true });
</script>
